import {Banner, Link} from '@primer/react'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {useState} from 'react'
import {clsx} from 'clsx'
import styles from './DecoupledModeBanner.module.css'

// Banner auto-expires on April 18, 2026 (2 months from launch)
const BANNER_EXPIRY_DATE = new Date('2026-04-18T00:00:00Z')

interface DecoupledModeBannerProps {
  enabled?: boolean
  className?: string
  dismissalKey?: string
}

type DismissalSurfaceArea = 'cca' | 'issues'

// Right now, both CCA and Issues use the same dismissal key, but this allows us to easily separate them in the future.
// To change this in the future, we'll also need to update the backend `Issues::AgentTasksController` to reference
// new issue-specific dismissal key.
export const DISMISSAL_KEY: Record<DismissalSurfaceArea, string> = {
  cca: 'copilot_coding_agent_decoupled_mode_banner',
  issues: 'copilot_coding_agent_decoupled_mode_banner',
} as const

export function DecoupledModeBanner({
  enabled = true,
  className,
  dismissalKey = DISMISSAL_KEY.cca,
}: DecoupledModeBannerProps) {
  const [dismissed, setDismissed] = useState(false)

  // Don't show banner after expiry date
  if (new Date() > BANNER_EXPIRY_DATE) {
    return null
  }

  if (!enabled || dismissed) {
    return null
  }

  return (
    <Banner
      variant="info"
      title="Sessions no longer create pull requests by default."
      hideTitle
      className={clsx(styles.banner, className)}
      onDismiss={async () => {
        setDismissed(true)
        await verifiedFetch(`/settings/dismiss-notice/${dismissalKey}`, {method: 'POST'})
      }}
    >
      <Banner.Description>
        <span className="text-semibold">Sessions no longer create pull requests by default.</span> Ask for a pull
        request in your prompt or open one when the session is complete.{' '}
        <Link inline href="https://docs.github.com/early-access/github/articles/cca-march-update" target="_blank">
          Learn more.
        </Link>
      </Banner.Description>
    </Banner>
  )
}
